// The 42 custom-field fieldsets, as display metadata for the scope screen.
//
// Keys are the canonical camelCase POST enum values, captured verbatim from
// the live OpenAPI spec for POST /v1/custom-fields (2026-07-29). Live
// post-options remains the authority on what a fieldset accepts — this
// registry only provides labels and grouping so the scope screen can render
// a navigable picker. post-options option maps key fieldsets in PascalCase
// ("WorkRequestDetails"), so normalizeFieldset() accepts both casings.
//
// Pure module: no React, no Supabase, no fetch.

export const FIELDSET_GROUPS = [
  {
    group: 'Work requests',
    fieldsets: [
      { key: 'workRequestDetails',              label: 'Work request details' },
      { key: 'workRequestAssignment',           label: 'Work request assignment' },
      { key: 'workRequestResponse',             label: 'Work request response' },
      { key: 'workRequestResolution',           label: 'Work request resolution' },
      { key: 'workRequestFinalization',         label: 'Work request finalization' },
      { key: 'workRequestOnHoldPlacement',      label: 'Work request on-hold placement' },
      { key: 'workRequestDeletion',             label: 'Work request deletion' },
    ],
  },
  {
    group: 'Planned maintenance (work tasks)',
    fieldsets: [
      { key: 'workTaskDetails',                 label: 'Work task details' },
      { key: 'workTaskInstructionSet',          label: 'Work task instruction set' },
      { key: 'workTaskExecution',               label: 'Work task execution' },
      { key: 'workTaskResponse',                label: 'Work task response' },
      { key: 'workTaskFinalization',            label: 'Work task finalization' },
    ],
  },
  {
    group: 'Scheduling',
    fieldsets: [
      { key: 'scheduleRequestDetails',          label: 'Schedule request details' },
      { key: 'scheduleRequestResponse',         label: 'Schedule request response' },
      { key: 'scheduleRequestApproval',         label: 'Schedule request approval' },
      { key: 'scheduleRequestDeclination',      label: 'Schedule request declination' },
      { key: 'scheduleRequestEstimate',         label: 'Schedule request estimate' },
      { key: 'scheduleRequestLegacyInvoice',    label: 'Schedule request legacy invoice' },
      { key: 'scheduleRequestLegacyPayment',    label: 'Schedule request legacy payment' },
    ],
  },
  {
    group: 'Transportation',
    fieldsets: [
      { key: 'transportationRequestDetails',      label: 'Transportation request details' },
      { key: 'transportationRequestAssignment',   label: 'Transportation request assignment' },
      { key: 'transportationRequestResponse',     label: 'Transportation request response' },
      { key: 'transportationRequestApproval',     label: 'Transportation request approval' },
      { key: 'transportationRequestDeclination',  label: 'Transportation request declination' },
      { key: 'transportationRequestFinalization', label: 'Transportation request finalization' },
    ],
  },
  {
    group: 'Equipment',
    fieldsets: [
      { key: 'equipmentItems',                  label: 'Equipment items' },
      { key: 'equipmentLogs',                   label: 'Equipment logs' },
      { key: 'downtimeEventStart',              label: 'Downtime event start' },
      { key: 'downtimeEventEnd',                label: 'Downtime event end' },
    ],
  },
  {
    group: 'Inventory',
    fieldsets: [
      { key: 'inventoryItems',                  label: 'Inventory items' },
      { key: 'inventoryLogs',                   label: 'Inventory logs' },
    ],
  },
  {
    group: 'Purchase orders',
    fieldsets: [
      { key: 'purchaseOrderDetails',            label: 'Purchase order details' },
      { key: 'purchaseOrderPurchase',           label: 'Purchase order purchase' },
      { key: 'purchaseOrderShipmentReceipt',    label: 'Purchase order shipment receipt' },
      { key: 'purchaseOrderLineItems',          label: 'Purchase order line items' },
    ],
  },
  {
    group: 'Invoices',
    fieldsets: [
      { key: 'invoiceDetails',                  label: 'Invoice details' },
      { key: 'invoiceResponse',                 label: 'Invoice response' },
      { key: 'invoicePayment',                  label: 'Invoice payment' },
    ],
  },
  {
    group: 'People & places',
    fieldsets: [
      { key: 'buildings',                       label: 'Buildings' },
      { key: 'resources',                       label: 'Resources' },
      { key: 'users',                           label: 'Users' },
    ],
  },
  {
    group: 'Utilities',
    fieldsets: [
      { key: 'utilityProviderBillEntry',        label: 'Utility provider bill entry' },
    ],
  },
];

export const ALL_FIELDSETS = FIELDSET_GROUPS.flatMap(g =>
  g.fieldsets.map(f => ({ ...f, group: g.group }))
);

const byKey = new Map(ALL_FIELDSETS.map(f => [f.key.toLowerCase(), f]));

// Accepts camelCase ("workRequestDetails") or the PascalCase shape
// post-options uses ("WorkRequestDetails"). Returns the canonical camelCase
// key, or null for anything unrecognized.
export function normalizeFieldset(value) {
  if (!value) return null;
  return byKey.get(String(value).trim().toLowerCase())?.key || null;
}

export function fieldsetLabel(key) {
  const norm = normalizeFieldset(key);
  return (norm && byKey.get(norm.toLowerCase())?.label) || String(key ?? '');
}

export function fieldsetGroup(key) {
  const norm = normalizeFieldset(key);
  return (norm && byKey.get(norm.toLowerCase())?.group) || null;
}
